/**
 * Shared identity used across every JSON-LD schema so the site's structured
 * data references ONE canonical Organization node (via @id).
 *
 * Compliance (see the `seo` skill + pam-persona): Pam REVIEWED the questions
 * (never "wrote"/"authored"); no pass guarantees; the pass gap is the ASWB's
 * own published data.
 */

export const SITE_URL = 'https://www.socialworktestprepacademy.com';
export const ORG_NAME = 'Social Work Test Prep Academy';
export const ORG_ALT_NAME = 'SWTPA';

/** Stable @id so Course/Product/FAQ can point at the same Organization node. */
export const ORG_ID = `${SITE_URL}/#organization`;

export const ORG_LOGO = `${SITE_URL}/logo512.png`;

/** The iOS app's App Store listing — a real, crawlable SWTPA property. */
export const APP_STORE_ID = '6748264360';
export const APP_STORE_URL = `https://apps.apple.com/us/app/social-work-test-prep-academy/id${APP_STORE_ID}`;

/**
 * Verified live profiles (footer + repo). Substack is the primary content channel.
 *
 * Every URL here must resolve to a page a crawler can actually read. The X/Twitter
 * handle was removed: it has no public content and serves 404 to crawlers, so it
 * was an entity-graph dead end rather than a corroborating signal.
 */
export const ORG_SAME_AS: readonly string[] = [
  'https://socialworktestprepacademy.substack.com',
  'https://www.instagram.com/socialworktestprepacademy',
  'https://www.facebook.com/profile.php?id=61565921293583',
  APP_STORE_URL,
];

/**
 * The founder Person entity — the E-E-A-T signal. A real LCSW who REVIEWS the
 * ASWB practice questions. Reused as `founder` on the Organization schema.
 */
export const FOUNDER_PERSON = {
  '@type': 'Person',
  name: 'Pamela Coin',
  jobTitle: 'Licensed Clinical Social Worker (LCSW)',
  description:
    'Licensed Clinical Social Worker and Board Approved Clinical Supervisor. She reviews the ASWB practice questions used throughout Social Work Test Prep Academy.',
  hasCredential: {
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: 'license',
    name: 'Licensed Clinical Social Worker (LCSW)',
  },
} as const;

/** Minimal Organization reference (by @id) for use as a `provider`/`brand`. */
export const ORG_REFERENCE = {
  '@type': 'EducationalOrganization',
  '@id': ORG_ID,
  name: ORG_NAME,
  url: SITE_URL,
} as const;
